import { usePlatformStatus } from './usePlatformStatus';
import { platformBaseUrl } from '../api/client';
import { Icon } from '../ui/Icon';
import { StatStrip } from '../ui/StatStrip';

const CHECK_LABELS: Record<string, string> = {
  database: 'Database',
  migrations: 'Schema migrations',
  storage: 'File storage',
};

/**
 * Can this deployment talk to its backend, and if not, which half is broken?
 *
 * It shows only what the API publishes. There is no host, port, database name
 * or driver error here, because an operator needs the verdict, not the topology.
 */
export function PlatformStatusPanel(): React.JSX.Element {
  const { phase, liveness, readiness, error, checkedAt, refresh } = usePlatformStatus();

  const overall =
    phase === 'loading'
      ? { tone: 'pending', label: 'Checking' }
      : phase === 'unreachable'
        ? { tone: 'down', label: 'Unreachable' }
        : readiness?.status === 'ready'
          ? { tone: 'up', label: 'Ready' }
          : { tone: 'down', label: 'Not ready' };

  const checksUp = readiness ? Object.values(readiness.checks).filter((state) => state === 'up').length : 0;
  const checksTotal = readiness ? Object.keys(readiness.checks).length : 0;

  const verdictLine =
    phase === 'loading'
      ? 'Asking the API how it is doing.'
      : phase === 'unreachable'
        ? 'The API did not answer, so nothing else here can be trusted.'
        : readiness?.status === 'ready'
          ? 'The API is up, and its database and schema are ready.'
          : 'The API answered, but at least one check below is failing.';

  return (
    <>
      <section className="card" aria-labelledby="overall-heading">
        <div className={`verdict verdict--${overall.tone}`}>
          <span className="verdict__light" aria-hidden="true" />
          <div className="verdict__text">
            <h2 id="overall-heading">Backend</h2>
            <p>{verdictLine}</p>
          </div>
          <span className={`pill pill--${overall.tone}`} data-testid="overall-status">
            {overall.label}
          </span>
        </div>

        <StatStrip
          stats={[
            {
              label: 'Checks up',
              value: checksTotal ? `${checksUp}/${checksTotal}` : '—',
              tone: checksTotal && checksUp === checksTotal ? 'up' : checksTotal ? 'down' : undefined,
            },
            { label: 'Uptime', value: liveness ? formatUptime(liveness.uptimeSeconds) : '—' },
            { label: 'Last checked', value: checkedAt ? checkedAt.toLocaleTimeString() : '—' },
          ]}
          testId="platform-stats"
        />

        <div className="panel">
          <dl className="facts">
            <div>
              <dt>API</dt>
              <dd>
                <code data-testid="api-base-url">{platformBaseUrl}</code>
              </dd>
            </div>
            <div>
              <dt>Contract version</dt>
              <dd data-testid="contract-version">{liveness?.version ?? '—'}</dd>
            </div>
          </dl>
        </div>
      </section>

      <section className="card" aria-labelledby="checks-heading">
        <div className="panel">
          <div className="panel__head">
            <h2 id="checks-heading">Readiness checks</h2>
            <button type="button" className="button button--small" onClick={refresh}>
              <Icon name="refresh" size={16} />
              Check again
            </button>
          </div>

          {phase === 'unreachable' ? (
            <p className="notice" data-testid="unreachable-notice" role="status">
              {error} Start the backend, or point <code>VITE_API_BASE_URL</code> at a running one.
            </p>
          ) : (
            <ul className="checks" data-testid="checks">
              {readiness
                ? Object.entries(readiness.checks).map(([name, state]) => (
                    <li key={name} className="checks__item" data-testid={`check-${name}`}>
                      <span>{CHECK_LABELS[name] ?? name}</span>
                      <span className={`pill pill--${state === 'up' ? 'up' : 'down'}`}>
                        {state === 'up' ? 'Up' : 'Down'}
                      </span>
                    </li>
                  ))
                : ['database', 'migrations'].map((name) => (
                    <li key={name} className="checks__item">
                      <span>{CHECK_LABELS[name]}</span>
                      <span className="pill pill--pending">Checking</span>
                    </li>
                  ))}
            </ul>
          )}
        </div>
      </section>
    </>
  );
}

function formatUptime(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ${seconds % 60}s`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${minutes % 60}m`;
}
